import m, {MithrilComponent} from '@bitstillery/mithril'
import {Button, Dialog, FieldCheckbox, FieldDate, FieldSelect, Icon, RadioGroup} from '@bitstillery/common/components/portal'
import {$t} from '@bitstillery/common/app'
import {$m, $s} from '@/app'
import {plausible_event} from '@bitstillery/common/lib/utils'
import {PlausibleEvent} from '@bitstillery/common/types/utils'
import {notifier} from '@bitstillery/common/app'
import {api} from '@bitstillery/common/app'
import {AvailabilityType} from '@bitstillery/common/api/fact2server'
import type {ConfirmOrderRequest} from '@bitstillery/common/api/fact2server'
import type {Instance as TippyInstance} from 'tippy.js'
import {conditional, required, validation} from '@bitstillery/common/lib/validation'
import type {Validator} from '@bitstillery/common/lib/validation'
import {state, type State} from '@bitstillery/mithril'

const DIALOG_CONFIRMATION_ID = 'dialog-confirmation'

function reset_dialog_confirmation_mount() {
    const element = document.querySelector(`#${DIALOG_CONFIRMATION_ID}`)
    const parent = element?.parentElement
    if (element) {
        m.mount(element, null)
        const new_element = document.createElement('div')
        new_element.id = DIALOG_CONFIRMATION_ID
        element.remove()
        parent?.append(new_element)
    }
}

/** Whether checkout books the cart or finalizes the order, as `$s.cart.finalize.selection` holds it. */
const FINALIZE_OPTIONS: [boolean, string][] = [
    // @i18n include: checkout.status.book
    [false, 'checkout.status.book'],
    // @i18n include: checkout.status.finalize
    [true, 'checkout.status.finalize'],
]

/**
 * Only single order promotions have a direct selection of promotion items, because
 * the promotion items are added as additional items to the current sales order. Promotions of
 * type multi-order may choose items during the promotion period, spanning multiple sales orders.
 */
interface CheckoutFormData {
    can_order: () => boolean
    can_order_blocker: () => string
    delivery: {
        options: string[][]
        selection: string
    }
    form: {
        t1: boolean
        tbo: boolean
        terms: boolean
    }
    loading: boolean
    lockedGiftPlaceholder: string
    requested_delivery_date: string | null
}

export class CheckoutForm extends MithrilComponent {
    data: State<CheckoutFormData> = state({
        // This is a metafield that is used to validate order conditions.
        can_order: () => !this.data.can_order_blocker,
        /**
         * What blocks `can_order`, phrased for the confirm button's tip. An empty cart has no field of
         * its own to carry a validation message, so without this the disabled button is silent about it.
         *
         * Booking an order is possible when there is a cart amount. Finalizing an order is possible when
         * there is either a booked amount or a cart amount.
         */
        can_order_blocker: () => {
            const has_order_amount = $s.cart.finalize.selection
                ? Boolean($m.cart.booked_amount()) || Boolean($m.cart.cart_amount())
                : Boolean($m.cart.cart_amount())
            if (!has_order_amount) {
                return $t('checkout.status.blocked_empty_cart')
            }
            if (Object.values(this.$v).some((i) => i._invalid)) {
                return $t('checkout.status.blocked_incomplete')
            }
            return ''
        },
        delivery: {
            options: [
                // @i18n include: checkout.delivery.asap
                ['asap', 'checkout.delivery.asap'],
                // @i18n include: checkout.delivery.date
                ['date', 'checkout.delivery.date'],
            ],
            selection: 'asap',
        },
        form: {
            t1: false,
            tbo: false,
            terms: false,
        },
        loading: false,
        lockedGiftPlaceholder: '',
        requested_delivery_date: null,
    })

    $v = {
        delivery: validation([this.data.delivery, 'selection'], {
            // Nothing renders this validation's label or message; the checkout only reads `_invalid`.
            label: '',
            message: '',
            validate: (model_value: string, validator: Validator) => {
                if (model_value === 'asap' || (model_value === 'date' && this.data.requested_delivery_date)) {
                    validator.message = ''
                    return false
                }

                validator.message = $t('status.error.order.delivery_invalid')
                return validator
            },
        }),
        promotion_choice: validation(
            [$s.promotion, '$current'],
            conditional(() => $s.promotion.unlocked && $s.promotion.type === 'single-order', required()),
        ),
        t1: validation(
            this.data.form.$t1,
            conditional(() => Object.values($s.cart.items).some((i) => i.case_customs_status === 'T1'), required()),
        ),
        tbo: validation(
            this.data.form.$tbo,
            conditional(() => Object.values($s.cart.items).some((i) => i.offer_item_type === AvailabilityType.Tbo), required()),
        ),
        terms: validation(this.data.form.$terms, required()),
    }

    override oninit() {
        // const delivery_date = order.requested_delivery_date
        // this.data.delivery.date = delivery_date
        // if (data.delivery.date) {
        //     data.delivery.selection = 'date'
        // } else {
        //     data.delivery.selection = 'asap'
        // }
    }

    async confirm_order() {
        // Checkout is only reachable with lines in the cart, which only exist on an order.
        const order_artkey = $s.cart.artkey
        if (!order_artkey) {
            return
        }
        this.data.loading = true

        const confirm_data: ConfirmOrderRequest = {
            request_delivery: $s.cart.finalize.selection,
            requested_delivery_date: this.data.delivery.selection === 'date' ? this.data.requested_delivery_date : null,
        }

        if ($s.promotion.type === 'single-order') {
            confirm_data.promotion_choice = $s.promotion.current
        }
        // Order amount rounded to 1000 is a custom dimension to filter on for plausible;
        // After the order.config API call, the amount is empty.
        const total_amount = $m.cart.total_amount()

        // A failed confirm may answer without a JSON body, which leaves result null.
        const {result, success} = await api.post(`portal/orders/${order_artkey}/confirm`, confirm_data, true)
        if (success) {
            this.data.loading = false
            if ($s.promotion.type === 'single-order') {
                // Clear any promotion choice that we made; it's already now added to the additional items.
                // A multi-order has its own sales order and is not affected by a single-order.
                $s.promotion.current = ''
            }
            // Clear ProductQuantity states after completing an order; otherwise
            // Adding an item after booking will continue with the previously
            // Select amount.
            $s.cart.items = {}

            if ($s.cart.finalize.selection) {
                notifier.notify($t('notifications.order_confirmed', {id: $s.cart.artkey}), 'info', undefined, 'cartRight')
                plausible_event(PlausibleEvent.ORDER, {
                    props: {
                        company_type: $s.identity.user.company_type,
                        currency: $s.identity.user.currency,
                        customs_status: $s.identity.user.customs_visibility,
                        language: $s.identity.user.language,
                        price_preference: $s.identity.user.price_preference,
                        step: 'order_finalized',
                    },
                    revenue: {
                        amount: total_amount,
                        currency: $s.identity.user.currency,
                    },
                })
                await m.route.set(`/my-portal/orders/${order_artkey}?confirm_order`)
            } else {
                notifier.notify($t('notifications.order_booked', {id: order_artkey}), 'info', undefined, 'cartRight')
                plausible_event(PlausibleEvent.ORDER, {
                    props: {
                        company_type: $s.identity.user.company_type,
                        currency: $s.identity.user.currency,
                        customs_status: $s.identity.user.customs_visibility,
                        language: $s.identity.user.language,
                        price_preference: $s.identity.user.price_preference,
                        step: 'order_booked',
                    },
                    revenue: {
                        amount: total_amount,
                        currency: $s.identity.user.currency,
                    },
                })
                void m.route.set(`/my-portal/orders/${order_artkey}`)
            }

            // Re-sync $s.cart with the server after confirming; without this the cart only
            // becomes correct again after a full page reload. Confirm mutates the order
            // server-side and load_current_order() (which also refreshes the active promotion
            // and voucher) reads back the authoritative state:
            // - Booking keeps the order PENDING and moves the cart lines into sales_order_items,
            //   so the booked items show up in the checkout right away instead of only on reload.
            // - Finalizing takes the order out of PENDING; current-order then hands back a fresh
            //   empty order, so $s.cart.artkey points at a valid new order for the next cart
            //   (otherwise the next items are posted to the finalized/`null` order and lost).
            await $m.order.load_current_order()
        } else {
            // Final validation failed; go back to the cart validation step.
            await m.route.set('/shop/checkout')

            if (
                result &&
                typeof result === 'object' &&
                'message' in result &&
                typeof result.message === 'string' &&
                result.message
            ) {
                notifier.notify(result.message, 'warning', undefined, 'profile')
            } else {
                notifier.notify($t('notifications.order_issue'), 'warning', undefined, 'cartRight')
            }
            plausible_event(PlausibleEvent.ORDER_INVALID, {
                props: {
                    company_type: $s.identity.user.company_type,
                    currency: $s.identity.user.currency,
                    customs_status: $s.identity.user.customs_visibility,
                    language: $s.identity.user.language,
                    price_preference: $s.identity.user.price_preference,
                    step: 'order_invalid',
                },
                revenue: {
                    amount: total_amount,
                    currency: $s.identity.user.currency,
                },
            })
            this.data.loading = false
        }
    }

    /** TBO help: render `Dialog` on `document` mount point so it is not clipped by `.c-panel-cart` (`overflow` / `backdrop-filter`). */
    mount_tbo_help_dialog() {
        reset_dialog_confirmation_mount()

        const element = document.querySelector(`#${DIALOG_CONFIRMATION_ID}`)
        if (!element) {
            return
        }

        const close = () => {
            reset_dialog_confirmation_mount()
            m.redraw()
        }

        m.mount(element, {
            view: () => (
                <Dialog className='dialog-portal' title={$t('order.agree_tbo_title')} onclose={close}>
                    <div className='agree-help'>
                        {m.trust(
                            $t('order.agree_tbo_tip', {
                                sales_manager: $s.identity.user.sales_manager.name,
                                terms_link:
                                    $s.portal.account.slug === 'msp'
                                        ? 'https://movingspirits.eu/terms-and-conditions/'
                                        : 'https://a2bc.com/terms-and-conditions/',
                            }),
                        )}
                    </div>
                </Dialog>
            ),
        })
    }

    view() {
        const has_errors = $m.cart.has_errors()
        return (
            <div className='confirmations'>
                {$s.promotion.active && $s.promotion.type === 'single-order' && (
                    <div className='confirmation active'>
                        <FieldSelect
                            disabled={!$s.promotion.unlocked}
                            help={$t('promotions.gifts.choose_help')}
                            label={$s.promotion.unlocked ? $t('promotions.gifts.choose') : $t('promotions.gifts.choose')}
                            model={$s.promotion.unlocked ? [$s.promotion, 'selection'] : [this.data, 'lockedGiftPlaceholder']}
                            options={$s.promotion.gifts.map((i) => ({label: i.description, value: i.id}))}
                            placeholder={
                                $s.promotion.unlocked
                                    ? $t('promotions.gifts.unlocked_placeholder')
                                    : $t('promotions.gifts.locked_placeholder')
                            }
                            validation={this.$v.promotion_choice}
                        />
                    </div>
                )}
                <div className='confirmation active'>
                    <RadioGroup
                        className='active'
                        disabled={$s.cart.loading || !$s.cart.artkey}
                        help={() => {
                            let description
                            if ($s.cart.finalize.selection) {
                                description = $t('checkout.status.finalize_help')
                            } else {
                                description = $t('checkout.status.book_help')
                            }
                            return description
                        }}
                        label={$t('order.completion')}
                        model={[$s.cart.finalize, 'selection']}
                        options={FINALIZE_OPTIONS}
                        translate={{prefix: ''}}
                    />
                </div>

                {/* Delivery preference only applies when finalizing; a booked order has no delivery yet. */}
                {$s.cart.finalize.selection && (
                    <div className='confirmation active'>
                        <RadioGroup
                            disabled={$s.cart.loading || !$s.cart.artkey}
                            label={$t('order.delivery')}
                            model={[this.data.delivery, 'selection']}
                            options={this.data.delivery.options}
                            translate={{prefix: ''}}
                        />

                        {this.data.delivery.selection === 'date' && (
                            <FieldDate
                                date_picker_options={{
                                    minDate: new Date(),
                                }}
                                label={$t('order.delivery_date')}
                                model={this.data.$requested_delivery_date}
                            />
                        )}
                    </div>
                )}

                <div className='confirmation active terms element'>
                    <FieldCheckbox
                        disabled={!$s.cart.artkey}
                        label={
                            <span>
                                {m.trust(
                                    $t('order.agree_tos', {
                                        link: '/terms-conditions',
                                    }),
                                )}
                            </span>
                        }
                        model={this.data.form.$terms}
                        validation={this.$v.terms}
                    />

                    {Object.values($s.cart.items).some((i) => i.case_customs_status === 'T1') && (
                        <FieldCheckbox
                            disabled={has_errors || !$s.cart.artkey}
                            label={$t('order.agree_t1')}
                            model={this.data.form.$t1}
                            validation={this.$v.t1}
                        />
                    )}

                    {Object.values($s.cart.items).some((i) => i.offer_item_type === AvailabilityType.Tbo) && (
                        <div className='tbo-checkbox'>
                            <FieldCheckbox
                                disabled={has_errors || !$s.cart.artkey}
                                help={$t('order.agree_tbo_help')}
                                model={this.data.form.$tbo}
                                validation={this.$v.tbo}
                            >
                                {$t('order.agree_tbo')}
                                <Icon
                                    onclick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        this.mount_tbo_help_dialog()
                                    }}
                                    name='question'
                                    size='s'
                                    type='info'
                                />
                            </FieldCheckbox>
                        </div>
                    )}
                </div>
                <div className='btn-cart-confirm'>
                    <Button
                        className='btn-confirm-step'
                        disabled={!this.data.can_order}
                        icon='checked'
                        loading={this.data.loading}
                        onclick={() => this.confirm_order()}
                        text={
                            $s.cart.finalize.selection
                                ? $t('checkout.status.step2_button')
                                : $t('checkout.status.step2_button_book')
                        }
                        tip={() => this.data.can_order_blocker}
                        // The tip only exists to explain a disabled button; an enabled one resolves to an
                        // empty string, and tippy skips showing an empty popper when `onShow` returns false.
                        tip_config={{
                            delay: 200,
                            onShow: (instance: TippyInstance) => (instance.props.content ? undefined : false),
                        }}
                        type='success'
                    />
                </div>
            </div>
        )
    }
}
